import DesignIconConstants from '~/constants/DesignIconConstants';
import {TRANSPARENT} from '~/constants/UploadConstants';
import {isSameColor, furthestMatch} from '~/utils/colors';
import {isNone} from '~/data/accessors/color';
import {UPLOAD_ONE_COLOR_FLAG} from '~/constants/FeatureFlags';
import {INK_COLORS} from '~/constants/InkConstants';
import {COLOR_MODES, DEFAULT_CONTRAST} from '~/constants/UploadConstants';
import {roundDecimal} from '~/shared/utils/math';
import {isArt, isNamesOrNumbers, isText, isUpload} from '~/data/accessors/icon';
import signaler from '~/vendor/signaler';

const iconTypeToSpotColorMap = {
  CLIPART: 'colors',
  UPLOAD: 'spotColors'
};

export const iconTypeToFriendlyName = iconType => {
  if (isUpload(iconType)) {
    return 'Upload';
  }
  if (isText(iconType)) {
    return 'Text';
  }
  if (isArt(iconType)) {
    return 'Art';
  }
  if (isNamesOrNumbers(iconType)) {
    return 'Names And Numbers';
  }
};

export const getColors = icon => {
  const {type} = icon;
  if (isUpload(type)) {
    return icon.spotColors;
  }
  if (isNamesOrNumbers(type) || isText(type)) {
    const textColors = [icon.color];
    if (iconHasOutline(icon)) {
      icon.outlineColor.isOutline = true;
      textColors.push(icon.outlineColor);
    }
    return textColors;
  }
  return icon.colors;
};

export const iconHasOutline = icon => {
  return (
    icon.outlineWidth &&
    icon.outlineWidth > 0 &&
    icon.outlineColor &&
    icon.outlineColor !== INK_COLORS.None
  );
};

export const getSubtleColors = (icon, subtleColors) => {
  if (isText(icon.type) && iconHasOutline(icon)) {
    return getColors(icon)
      .filter(textColor => textColor.isOutline)
      .filter(outlineColor => {
        return subtleColors.some(subtleColor =>
          isSameColor(subtleColor, outlineColor)
        );
      });
  } else if (icon.singleColor) {
    return subtleColors.filter(subtleColor => {
      return isSameColor(subtleColor, icon.color);
    });
  } else {
    return getColors(icon).filter(iconColor => {
      return subtleColors.some(subtleColor =>
        isSameColor(subtleColor, iconColor.user || iconColor)
      );
    });
  }
};

export const numberOfColors = icon => {
  const {type} = icon;
  return isUpload(type) ? icon.spotColors?.length : icon.colors?.length;
};

export const hasMultipleColors = icon => {
  return numberOfColors(icon) > 1;
};

const convertTransparentToConstant = spotColor => {
  const routine = color => (isNone(color) ? TRANSPARENT : color);

  return {
    ...spotColor,
    original: routine(spotColor.original),
    palette: routine(spotColor.palette),
    user: routine(spotColor.user)
  };
};

export function getContrastColorFullDesign(
  currentIcon,
  viewIcons,
  viewColors,
  designIcons,
  designColors
) {
  if (viewColors.length <= 1) {
    return getContrastColor(currentIcon, designIcons, designColors);
  } else {
    return getContrastColor(currentIcon, viewIcons, viewColors);
  }
}

export function getContrastColor(currentIcon, viewIcons, viewColors) {
  const {color: currentColor, id: currentId} = currentIcon;
  if (viewColors.length <= 1) {
    return INK_COLORS.Gray;
  }
  /*
  if 2 colors, always use the other for the outline
  if another text icon with the same text color has an outline, use the same outline color
  if another text icon of any color has an outline, use the same outline color (as long as it isn't the same color as the text)
  after that, fall back on any color in the design, using the highest contrast / color dif
  */
  if (viewColors.length === 2) {
    return viewColors.find(color => !isSameColor(currentColor, color));
  }

  let palette = [];
  const iconWithOutlines = viewIcons.filter(
    icon => icon.id !== currentId && icon.outlineColor && icon.outlineWidth
  );
  const sameColorWithOutlineText = iconWithOutlines.filter(icon =>
    isSameColor(icon.color, currentColor)
  );
  if (iconWithOutlines.length) {
    palette = sameColorWithOutlineText.length
      ? sameColorWithOutlineText.map(icon => icon.outlineColor)
      : iconWithOutlines
          .map(icon => icon.outlineColor)
          .filter(color => !isSameColor(currentColor, color));
  }
  if (!palette.length) {
    palette = viewColors;
  }
  palette = palette.filter(paletteColors => !isNone(paletteColors));
  return furthestMatch(currentColor, palette);
}

export function setDefaultFont(
  fontName = DesignIconConstants.TEXT.DEFAULTS.FONT_NAME,
  displayName = DesignIconConstants.TEXT.DEFAULTS.FONT_DISPLAY,
  fontSize = DesignIconConstants.TEXT.DEFAULTS.LINE_HEIGHT,
  mediumFontSize = DesignIconConstants.TEXT.DEFAULTS.MEDIUM_TEXT_FONT_HEIGHT,
  longFontSize = DesignIconConstants.TEXT.DEFAULTS.LONG_TEXT_FONT_HEIGHT
) {
  DesignIconConstants.TEXT.DEFAULTS.FONT_NAME = fontName;
  DesignIconConstants.TEXT.DEFAULTS.FONT_DISPLAY = displayName;
  DesignIconConstants.TEXT.DEFAULTS.LINE_HEIGHT = fontSize;
  DesignIconConstants.TEXT.DEFAULTS.MEDIUM_TEXT_FONT_HEIGHT = mediumFontSize;
  DesignIconConstants.TEXT.DEFAULTS.LONG_TEXT_FONT_HEIGHT = longFontSize;
}

export function buildImageChangerConfig(icon, scale) {
  const formatSize = value => roundDecimal(value * scale, 0);

  const width = icon.width;
  const colors = icon[iconTypeToSpotColorMap[icon.type]];

  const hasChangedColors = colors.some(
    color => !isSameColor(color.palette, color.user)
  );

  const config = {
    iconType: icon.type,

    crop: icon.crop,

    resize: {
      width: formatSize(width, scale)
    },

    rotate: {
      angle: icon.rotation
    },

    flip: {
      horizontal: icon.horizontalFlip,
      vertical: icon.verticalFlip
    }
  };

  if (hasChangedColors) {
    config.editColors = {
      colors: colors.map(convertTransparentToConstant)
    };
  }

  if (icon.singleColor) {
    if (icon.contrast) {
      config.colorizeSolid = {
        color: icon.color,
        contrast: icon.contrast,
        invertContrast: icon.invertContrast
      };
    } else {
      config.colorize = {color: icon.color};
    }
  }

  return config;
}

// This is a mutable function. iconData passed in will change.
export const setIconDecorationOverrideColors = ({
  iconType,
  iconData,
  decorationOverrideColor
}) => {
  iconData.color = decorationOverrideColor;

  switch (iconType) {
    case DesignIconConstants.TYPES.clipart:
      iconData.singleColor = true;
      if (iconData.colors.size > 1 && !iconData.blackwhite) {
        // CGIR does not support keep-white for multicolor cliparts
        iconData.transparent = true;
      }
      break;
    case DesignIconConstants.TYPES.upload: {
      const testValue = signaler.featureFlag(UPLOAD_ONE_COLOR_FLAG);
      if (testValue === 'test') {
        iconData.contrast = DEFAULT_CONTRAST;
      }
      iconData.singleColor = true;
      break;
    }
    default:
      break;
  }
  return iconData;
};

export const selectedIconHasUpdated = ({
  selectedIcon,
  previouslySelectedIcon
}) => {
  if (!selectedIcon || !previouslySelectedIcon) return false;
  return selectedIcon && selectedIcon.id !== previouslySelectedIcon?.id;
};

export const isFullColor = ({colorMode, singleColor}) => {
  return colorMode === COLOR_MODES.PHOTO && !singleColor;
};

export const getClampedIconDimensionForConstraint = ({
  icon,
  constraint,
  dimension
}) => {
  if (!icon || !constraint || !icon[dimension] || !constraint[dimension]) {
    return null;
  }
  return icon[dimension] > constraint[dimension]
    ? constraint[dimension] * 0.97
    : icon[dimension];
};

export const getClampedIconDimensions = ({icon, constraint}) => {
  const width = getClampedIconDimensionForConstraint({
    icon,
    dimension: 'width',
    constraint
  });
  const height = getClampedIconDimensionForConstraint({
    icon,
    dimension: 'height',
    constraint
  });
  return {width, height};
};

export const getDimensionsForAspectRatio = ({icon, iconWidth, iconHeight}) => {
  if (
    isNaN(icon?.width) ||
    isNaN(icon?.height) ||
    isNaN(iconWidth) ||
    isNaN(iconHeight)
  ) {
    return null;
  }

  const imageAspectRatio = icon.width / icon.height;
  let width = iconWidth;
  let height = iconHeight;

  if (imageAspectRatio > 1) {
    height = roundDecimal(iconWidth / imageAspectRatio, 4);
  } else {
    width = roundDecimal(iconHeight * imageAspectRatio, 4);
  }

  return {width, height};
};

export const detectSpecialConditions = designIcons => {
  let hasFullColorIcon = false;
  let hasNamesNumberIcon = false;

  designIcons?.forEach(icon => {
    if (
      isFullColor({
        colorMode: icon?.colorMode,
        singleColor: icon?.singleColor
      })
    ) {
      hasFullColorIcon = true;
    }

    if (isNamesOrNumbers(icon?.type)) {
      hasNamesNumberIcon = true;
    }
  });

  return {
    hasFullColorIcon,
    hasNamesNumberIcon
  };
};

export const hasValidCrop = (crop = undefined, skipConversion = false) => {
  if (!crop) return false;
  if (!skipConversion) crop = crop.toJS();
  return (
    !!crop.heightPercent &&
    !!crop.widthPercent &&
    (crop.widthPercent < 0.99 || crop.heightPercent < 0.99)
  );
};
